import { Api } from './axios-config';
import { IPagedResult } from './pagedResult';

export interface IClientView {
  clientId: number;
  userId?: number;
  name: string;
  email?: string;
  phone?: string;
  phoneConfirmed?: boolean;
  notes?: string;
  lastVisitDate?: string;
  totalAppointments: number;
  lastAppointment?: Date;
  allowMarketing: boolean;
}

export interface IClientAppointmentHistory {
  appointmentId: number;
  appointmentNumber: number;
  date: Date;
  serviceName: string;
  employeeName: string;
  totalAmount: number;
  paymentMethodName: string;
  completed: boolean;
  canceled: boolean;
}

export interface IClientFilterParams {
  lastVisitBeforeDays?: number;
  lastVisitAfterDays?: number;
  minCompletedAppointments?: number;
  maxCompletedAppointments?: number;
  whatsAppConfirmed?: boolean;
}

const getClients = async (businessId: number, page: number, pageSize: number, search?: string, filterParams?: IClientFilterParams): Promise<IPagedResult<IClientView>> => {
  const params = new URLSearchParams();
  params.append('businessId', businessId.toString());
  params.append('page', page.toString());
  params.append('pageSize', pageSize.toString());
  if (search) params.append('search', search);
  if (filterParams) {
    if (filterParams.lastVisitBeforeDays !== undefined) params.append('lastVisitBeforeDays', filterParams.lastVisitBeforeDays.toString());
    if (filterParams.lastVisitAfterDays !== undefined) params.append('lastVisitAfterDays', filterParams.lastVisitAfterDays.toString());
    if (filterParams.minCompletedAppointments !== undefined) params.append('minCompletedAppointments', filterParams.minCompletedAppointments.toString());
    if (filterParams.maxCompletedAppointments !== undefined) params.append('maxCompletedAppointments', filterParams.maxCompletedAppointments.toString());
    if (filterParams.whatsAppConfirmed !== undefined) params.append('whatsAppConfirmed', filterParams.whatsAppConfirmed.toString());
  }

  const { data } = await Api.get<IPagedResult<IClientView>>(`/client?${params.toString()}`);
  return data;
};

/** Simple client search for autocomplete — returns flat array of matches */
const searchClients = async (businessId: number, search?: string): Promise<IClientView[]> => {
  const { data } = await Api.get<IPagedResult<IClientView>>(`/client?businessId=${businessId}&page=1&pageSize=100${search ? `&search=${encodeURIComponent(search)}` : ''}`);
  return data.items;
};

const getHistory = async (userId: number, businessId: number): Promise<IClientAppointmentHistory[]> => {
  const params = new URLSearchParams();
  params.append('businessId', businessId.toString());

  const { data } = await Api.get<IClientAppointmentHistory[]>(`/client/${userId}/history?${params.toString()}`);
  return data;
};

const getBirthdays = async (businessId: number, months = 1): Promise<IClientView[]> => {
  const params = new URLSearchParams();
  params.append('businessId', businessId.toString());
  params.append('months', months.toString());

  const { data } = await Api.get<IClientView[]>(`/client/birthdays?${params.toString()}`);
  return data;
};

export interface IUpdateClient {
  clientId: number;
  businessId: number;
  name: string;
  email?: string;
  phone?: string;
  notes?: string;
}

const updateClient = async (body: IUpdateClient): Promise<void> => {
  await Api.put('/client', body);
};

const updateMarketing = async (clientId: number, businessId: number, allowMarketing: boolean): Promise<void> => {
  await Api.patch(`/client/${clientId}/marketing`, { businessId, allowMarketing });
};

export const clientService = {
  getClients,
  searchClients,
  getHistory,
  getBirthdays,
  updateClient,
  updateMarketing,
};
